// ============================================================================
// CHANGO V5.0 — <ShareManagerPanel> — D5.3 tenant-side share link manager
// ============================================================================
// Mounted on /app/quotes/:id (and available for the future /shares route).
//
// Role policy (mirrors server/routes/shares.js):
//   - boss / finance   : may create shares with either preset, revoke anything.
//   - sales            : may create shares with `sales_safe` preset ONLY,
//                        and may revoke ONLY their own shares. The UI hides
//                        the internal preset toggle for sales; the backend
//                        double-checks.
//
// §20 #17 discipline:
//   The list rendering intentionally shows only {token (partial), preset,
//   status, expires_at, access_count/max_accesses, has_password}. No amount
//   or margin data ever touches this panel. Previewing the shared quote is
//   done by opening /s/<token> in a new tab, which goes through the same
//   redactQuoteForShare path the public viewer uses.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { api, HttpError } from '../api/client.js';
import { useAuth } from '../hooks/useAuth.js';

const PRESET_LABEL = Object.freeze({
  sales_safe: '对外（隐藏成本/利润）',
  internal:   '内部（完整金额）',
});

const STATUS_META = Object.freeze({
  ok:                { label: '可访问',      tone: 'bg-emerald-100 text-emerald-800' },
  password_required: { label: '需密码',      tone: 'bg-amber-100 text-amber-800' },
  expired:           { label: '已过期',      tone: 'bg-slate-200 text-slate-600' },
  exhausted:         { label: '次数用尽',    tone: 'bg-slate-200 text-slate-600' },
  revoked:           { label: '已撤销',      tone: 'bg-rose-100 text-rose-800' },
});

const DEFAULT_EXPIRES_DAYS = 14;
const DEFAULT_MAX_ACCESS = 20;

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('zh-CN', { hour12: false });
}

function truncateToken(tok) {
  if (typeof tok !== 'string' || tok.length < 10) return tok || '';
  return `${tok.slice(0, 6)}…${tok.slice(-4)}`;
}

export default function ShareManagerPanel({ quoteId }) {
  const { user } = useAuth();
  const role = user?.role || 'sales';
  const isSales = role === 'sales';

  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null);

  // Create-form state
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState('sales_safe');
  const [expiresInDays, setExpiresInDays] = useState(DEFAULT_EXPIRES_DAYS);
  const [maxAccesses, setMaxAccesses] = useState(DEFAULT_MAX_ACCESS);
  const [password, setPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState(null);

  const reload = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        scope_type: 'quote',
        scope_id: String(quoteId),
      }).toString();
      const data = await api.get(`/api/shares?${qs}`);
      // Backend returns { items: [...] } (see server/routes/shares.js GET /).
      setShares(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setBanner({
        tone: 'err',
        text: err instanceof HttpError ? `${err.code}: ${err.message}` : '加载失败',
      });
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Sales is locked to sales_safe preset even if the state gets out of sync.
  useEffect(() => {
    if (isSales && preset !== 'sales_safe') setPreset('sales_safe');
  }, [isSales, preset]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    setCreateErr(null);
    try {
      const payload = {
        scope_type: 'quote',
        scope_id: Number(quoteId),
        redaction_preset: preset,
        expires_in_days: Number(expiresInDays) || null,
        max_accesses: Number(maxAccesses) || null,
      };
      // Only include password when the user typed something; omitting null
      // so the backend can normalize to NULL (no password).
      if (password && password.length >= 4) payload.password = password;
      const res = await api.post('/api/shares', payload);
      // Copy the full URL to the clipboard if possible.
      const shareUrl = composeShareUrl(res?.token);
      try {
        if (navigator?.clipboard?.writeText && shareUrl) {
          await navigator.clipboard.writeText(shareUrl);
          setBanner({ tone: 'ok', text: `链接已复制：${shareUrl}` });
        } else {
          setBanner({ tone: 'ok', text: `已创建分享链接：${shareUrl}` });
        }
      } catch {
        setBanner({ tone: 'ok', text: `已创建分享链接：${shareUrl}` });
      }
      setOpen(false);
      setPassword('');
      await reload();
    } catch (err) {
      setCreateErr(err instanceof HttpError ? `${err.code}: ${err.message}` : '创建失败');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (shareId) => {
    const ok = window.confirm('确定撤销此分享链接？此操作不可撤回。');
    if (!ok) return;
    try {
      await api.delete(`/api/shares/${shareId}`);
      setBanner({ tone: 'ok', text: '已撤销' });
      await reload();
    } catch (err) {
      setBanner({
        tone: 'err',
        text: err instanceof HttpError ? `${err.code}: ${err.message}` : '撤销失败',
      });
    }
  };

  const canCreate = useMemo(() => !!quoteId, [quoteId]);

  return (
    <section className="card mt-6 p-4" aria-label="分享链接">
      <header className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">分享链接</h3>
        {canCreate && (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? '取消' : '+ 生成新链接'}
          </button>
        )}
      </header>

      {banner && (
        <div
          role="status"
          className={[
            'mb-3 rounded px-3 py-2 text-sm',
            banner.tone === 'err'
              ? 'bg-rose-50 text-rose-700'
              : 'bg-emerald-50 text-emerald-700',
          ].join(' ')}
        >
          {banner.text}
        </div>
      )}

      {open && (
        <form onSubmit={onCreate} className="mb-4 grid gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">隐私预设</span>
              <select
                value={preset}
                onChange={(e) => setPreset(e.target.value)}
                className="field"
                disabled={isSales}
                aria-label="隐私预设"
              >
                <option value="sales_safe">{PRESET_LABEL.sales_safe}</option>
                {!isSales && <option value="internal">{PRESET_LABEL.internal}</option>}
              </select>
              {isSales && (
                <span className="text-[11px] text-slate-400">销售仅可生成对外链接。</span>
              )}
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">有效期（天）</span>
              <input
                type="number"
                min={1}
                max={90}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
                className="field"
                aria-label="有效期天数"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-slate-500">最大访问次数</span>
              <input
                type="number"
                min={1}
                max={10000}
                value={maxAccesses}
                onChange={(e) => setMaxAccesses(e.target.value)}
                className="field"
                aria-label="最大访问次数"
              />
            </label>
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-slate-500">访问密码（可留空）</span>
            <input
              type="password"
              autoComplete="new-password"
              minLength={0}
              maxLength={128}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field"
              placeholder="至少 4 位；留空表示无密码"
              aria-label="访问密码"
            />
          </label>
          {createErr && <p className="err text-rose-700">{createErr}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
              取消
            </button>
            <button type="submit" className="btn-primary" disabled={creating}>
              {creating ? '生成中…' : '生成链接'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="text-sm text-slate-400">加载中…</p>
      ) : shares.length === 0 ? (
        <p className="text-sm text-slate-400">
          尚未生成任何分享链接。点击「生成新链接」创建对外链接。
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {shares.map((s) => (
            <ShareRow
              key={s.id}
              share={s}
              isSales={isSales}
              currentUserId={user?.id}
              onRevoke={revoke}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function ShareRow({ share, isSales, currentUserId, onRevoke }) {
  const meta = STATUS_META[share.status] || STATUS_META.ok;
  const url = composeShareUrl(share.token);
  // Sales may only revoke their own shares; hide the button otherwise.
  const canRevoke =
    !share.revoked_at &&
    (!isSales || Number(share.created_by) === Number(currentUserId));

  const copy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
    } catch { /* best effort */ }
  };

  return (
    <li className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span
            className={[
              'inline-block rounded px-2 py-0.5 text-xs font-medium',
              meta.tone,
            ].join(' ')}
          >
            {meta.label}
          </span>
          <span className="font-mono text-xs text-slate-600">
            {truncateToken(share.token)}
          </span>
          <span className="text-xs text-slate-400">
            · {PRESET_LABEL[share.redaction_preset] || share.redaction_preset}
          </span>
          {share.has_password && (
            <span className="text-xs text-amber-700">· 🔒 加密</span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-slate-500">
          访问 {share.access_count ?? 0}
          {share.max_accesses != null ? `/${share.max_accesses}` : ''} 次 · 过期{' '}
          {formatDate(share.expires_at)} · 创建{' '}
          {formatDate(share.created_at)}
        </div>
      </div>
      <div className="flex flex-none gap-2">
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="btn-ghost text-xs"
        >
          打开
        </a>
        <button type="button" className="btn-ghost text-xs" onClick={copy}>
          复制链接
        </button>
        {canRevoke && (
          <button
            type="button"
            className="btn-ghost text-xs text-rose-700 hover:bg-rose-50"
            onClick={() => onRevoke(share.id)}
          >
            撤销
          </button>
        )}
      </div>
    </li>
  );
}

function composeShareUrl(token) {
  if (!token) return '';
  // Public viewer lives at /s/:token (SPA route) which internally calls
  // /public/share/:token/view. Using location.origin keeps dev/prod happy.
  if (typeof window === 'undefined') return `/s/${token}`;
  return `${window.location.origin}/s/${token}`;
}
